import { runTrackedAudioWork, type AudioWorkContext } from '../../audioWorkBarrier';
import {
  getBackupBlob,
  getChunkBlob,
  getPendingBackups,
  getPendingChunks,
  getRetainedFailedBytes,
  hasDrainableRetainedRecordingChunks,
  importLegacyData,
  markBackupAbandoned,
  markChunkAbandoned,
  normalizeIncompleteRecordingsForRecovery,
  purgeForeignRecordings,
  type PendingBackup,
  type PendingChunk,
  type RecordingKey,
  type RecoveryNormalizationOptions,
  type RecoveryRowFilter,
} from '../../persistenceV2';
import { type BackupRecording, type SessionChunk } from '../../types';
import { logger } from '../lib/logger';
import { processBackupV2 } from './backupV2';
import { maybeCompleteRecordingV2, processChunkV2 } from './processChunkV2';
import {
  markAdoptableTail,
  releaseCaptureLock,
  tryAcquireCaptureLock,
} from './recordingLockV2';
import { cleanupRecordingsV2 } from './recordingV2';
import { runRecoveryQueue } from './recoveryScheduler';
import { oweBacklogScan, pokeRetryDrain } from './retryDrainV2';
import { type GetState, type SetState } from './storeTypes';

// Five times the longest chunk cadence: a live recorder writes inside this
// window, a crashed one stopped the moment its tab died.
const DRAIN_ACTIVITY_GRACE_MS = 5 * 60_000;
// Tighter, because the boundary write cannot be taken back — a straggler chunk
// landing after the pass would find its recording already declared finished.
const NORMALIZE_ACTIVITY_GRACE_MS = 2 * 60_000;
// Without Web Locks nothing proves capture is idle, so age is the only cut left;
// a recording past this is abnormally long rather than live.
const NO_LOCKS_LIVENESS_GRACE_MS = 4 * 60 * 60_000;
// Past the cutoff, so the re-scan does not race the clock it is measured against.
export const DEFERRED_RETRY_SLACK_MS = 1_000;
// A stamp ahead of the clock would otherwise overflow the timer and fire it at once.
const DEFERRED_RETRY_MAX_MS = DRAIN_ACTIVITY_GRACE_MS;

export type LedgerBacklogDrainSource = 'active-ledger' | 'legacy-rollback';
/** Whether the row scan ran. An empty scan is 'scanned'; a skipped one is not. */
export type LedgerBacklogDrainResult = 'scanned' | 'skipped';
type RecoveryItemResult = 'abandoned' | 'processed' | 'deferred';

// The backlog now shares the ledger and the network with a live capture, and a
// slot is held until its upload settles — so this also caps blobs in memory.
const RECOVERY_DISPATCH_CONCURRENCY = 2;

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

let deferredRetry: { timer: ReturnType<typeof setTimeout>; at: number } | null = null;
// A normalize pass skipped under a live recording. A fully uploaded crashed row
// has no unsettled child to bring a later scan back, so the debt itself must.
let normalizeOwed = false;

export const isNormalizeOwed = (): boolean => normalizeOwed;

const clearDeferredTimer = (): void => {
  if (deferredRetry) clearTimeout(deferredRetry.timer);
  deferredRetry = null;
};

/** Called from the drain's reset, which runs on fence close. */
export const clearDeferredRecovery = (): void => {
  clearDeferredTimer();
  normalizeOwed = false;
};

// Hydration runs once per page, so the cutoff itself re-pokes the drain: one
// timer, always for the earliest cutoff.
const scheduleDeferredRecovery = (
  set: SetState,
  get: GetState,
  retryAt: number
): void => {
  if (deferredRetry && deferredRetry.at <= retryAt) return;
  clearDeferredTimer();
  const delay = Math.min(Math.max(retryAt - Date.now(), 0), DEFERRED_RETRY_MAX_MS);
  const timer = setTimeout(() => {
    deferredRetry = null;
    pokeRetryDrain(set, get, 'liveness-expired');
  }, delay + DEFERRED_RETRY_SLACK_MS);
  deferredRetry = { timer, at: retryAt };
};

/**
 * The gate `waitForChunks` applies, at the other place a recording can be
 * declared finished: audio the retry buffer can still deliver must reach the
 * note. Exhausted or released chunks are settled and need no hold.
 */
const completeUnlessDraining = async (
  set: SetState,
  get: GetState,
  key: RecordingKey,
  context: AudioWorkContext
): Promise<void> => {
  if (hasDrainableRetainedRecordingChunks(key)) {
    logger.info({
      event_type: 'RECOVERY_COMPLETION_DEFERRED_DRAINING',
      details: { sessionId: key.sessionId, recordingId: key.recordingId },
    });
    return;
  }
  await maybeCompleteRecordingV2(set, get, key, context);
};

const recoverChunk = async (
  set: SetState,
  get: GetState,
  { chunk, recording }: PendingChunk,
  context: AudioWorkContext
): Promise<RecoveryItemResult> => {
  context.throwIfStale();
  const blob = await getChunkBlob(chunk);
  context.throwIfStale();
  if (!blob) {
    logger.warn({
      event_type: 'HYDRATE_CHUNK_BLOB_MISSING',
      details: { sessionId: chunk.sessionId, index: chunk.index },
    });
    await markChunkAbandoned(chunk);
    await completeUnlessDraining(set, get, chunk, context);
    return 'abandoned';
  }

  const sessionChunk: SessionChunk = {
    sessionId: chunk.sessionId,
    recordingId: chunk.recordingId,
    index: chunk.index,
    id: chunk.id,
    isProcessed: false,
    wasMissingChunk: chunk.wasMissingChunk,
    size: chunk.size,
    duration: chunk.duration,
    producedAt: chunk.producedAt,
    blob,
  };
  const outcome = await processChunkV2(
    set,
    get,
    sessionChunk,
    recording.useTranscriptionServiceV2,
    context,
    'recovery'
  );
  return outcome === 'deferred' ? 'deferred' : 'processed';
};

const recoverBackup = async (
  set: SetState,
  get: GetState,
  { backup }: PendingBackup,
  context: AudioWorkContext
): Promise<RecoveryItemResult> => {
  context.throwIfStale();
  const blob = await getBackupBlob(backup);
  context.throwIfStale();
  if (!blob) {
    logger.warn({
      event_type: 'HYDRATE_BACKUP_BLOB_MISSING',
      details: { sessionId: backup.sessionId },
    });
    await markBackupAbandoned(backup);
    await completeUnlessDraining(set, get, backup, context);
    return 'abandoned';
  }

  const backupRecording: BackupRecording = {
    sessionId: backup.sessionId,
    recordingId: backup.recordingId,
    isProcessed: false,
    blob,
  };
  const outcome = await processBackupV2(set, get, backupRecording, context, 'recovery');
  return outcome === 'deferred' ? 'deferred' : 'processed';
};

/**
 * Gives crashed rows the end boundary completion needs, under the recording
 * lock. Web Locks release the instant a tab dies, so a free recording lock is
 * what separates a crash from a paused session that is silent but still alive,
 * and holding it for the pass also keeps a racing start out of those rows.
 */
const normalizeCrashedRecordings = async (
  set: SetState,
  get: GetState,
  currentUserRefId: string,
  source: LedgerBacklogDrainSource,
  defer: (retryAt: number) => void
): Promise<void> => {
  const recordingLock = await tryAcquireCaptureLock('recording');
  if (recordingLock.status === 'unavailable') {
    logger.info({
      event_type: 'LEDGER_CRASH_NORMALIZE_SKIPPED_CAPTURE_ACTIVE',
      details: { source },
    });
    normalizeOwed = true;
    oweBacklogScan();
    return;
  }
  // Every row this pass touches went quiet minutes ago, so a start racing it is
  // a fresh recording, not a competitor — let it adopt rather than be refused.
  markAdoptableTail(recordingLock.token);

  let normalized: RecordingKey[];
  try {
    const now = Date.now();
    const liveness: RecoveryNormalizationOptions =
      recordingLock.status === 'unsupported'
        ? { createdBefore: now - NO_LOCKS_LIVENESS_GRACE_MS }
        : {
            inactiveSince: now - NORMALIZE_ACTIVITY_GRACE_MS,
            onDeferred: (lastActivityAt) =>
              defer(lastActivityAt + NORMALIZE_ACTIVITY_GRACE_MS),
          };
    normalized = await normalizeIncompleteRecordingsForRecovery(
      currentUserRefId,
      liveness
    );
    normalizeOwed = false;
  } finally {
    // Only the boundary write needs the lock; a start must not wait behind the
    // completions it releases. A no-op once a racing start adopted the lease.
    releaseCaptureLock(recordingLock.token);
  }

  await Promise.all(
    normalized.map((key) =>
      runTrackedAudioWork(
        (context) => completeUnlessDraining(set, get, key, context),
        'other'
      )
    )
  );
};

/**
 * Drains rows already written to the ledger without consulting pipeline mode:
 * the rollout flag chooses the next writer, it never hides recoverable audio.
 *
 * Resolving is not scanning — a lock held elsewhere, or any failure on the way
 * to the scan, ends the drain quietly and must not discharge the debt.
 */
export const drainLedgerBacklogV2 = async (
  set: SetState,
  get: GetState,
  currentUserRefId: string,
  source: LedgerBacklogDrainSource
): Promise<LedgerBacklogDrainResult> => {
  const captureLock = await tryAcquireCaptureLock('recovery');
  if (captureLock.status === 'unavailable') {
    logger.info({
      event_type: 'LEDGER_BACKLOG_DRAIN_SKIPPED_DRAIN_ACTIVE',
      details: { source },
    });
    // Unlatch so a later hydration retries once the other drain finishes —
    // the runbook promises this skip is a deferred retry, not data loss.
    set({ hydrated: false });
    return 'skipped';
  }

  // Capture runs alongside this drain now, so recency is what keeps a live
  // recording's chunks out of the dispatch: it writes every minute or so, while
  // a crashed one went quiet at the crash. Finished rows bypass it in the filter.
  let retryAt = Infinity;
  const defer = (at: number) => {
    retryAt = Math.min(retryAt, at);
  };
  const recoveryFilter: RecoveryRowFilter = {
    userRefId: currentUserRefId,
    inactiveSince: Date.now() - DRAIN_ACTIVITY_GRACE_MS,
    onDeferred: (lastActivityAt) => defer(lastActivityAt + DRAIN_ACTIVITY_GRACE_MS),
  };

  let scanned = false;
  try {
    await purgeForeignRecordings(currentUserRefId);

    try {
      await normalizeCrashedRecordings(set, get, currentUserRefId, source, defer);
    } catch (err) {
      logger.error({
        event_type: 'LEDGER_CRASH_NORMALIZE_FAILED',
        details: { error: errorMessage(err), source },
      });
      // Retry normalization on a later hydration without suppressing recovery
      // uploads that can still be scheduled by this pass.
      set({ hydrated: false });
    }

    try {
      const retainedBytes = await getRetainedFailedBytes();
      if (retainedBytes > 0) {
        logger.info({
          event_type: 'RETAINED_FAILED_BYTES',
          details: { retainedBytes, source },
        });
      }
    } catch {
      // Telemetry is best effort.
    }

    let pendingChunks;
    let pendingBackups;
    try {
      [pendingChunks, pendingBackups] = await Promise.all([
        getPendingChunks(recoveryFilter),
        getPendingBackups(recoveryFilter),
      ]);
    } catch (err) {
      logger.error({
        event_type: 'HYDRATE_SCAN_FAILED',
        details: { error: errorMessage(err), source },
      });
      // Retry hydration later because no recovery work was scheduled.
      set({ hydrated: false });
      return 'skipped';
    }
    scanned = true;

    logger.info({
      event_type: 'LEDGER_BACKLOG_SCAN',
      details: {
        source,
        pendingChunks: pendingChunks.filter(({ chunk }) => chunk.status === 'pending')
          .length,
        failedChunks: pendingChunks.filter(({ chunk }) => chunk.status === 'failed')
          .length,
        pendingBackups: pendingBackups.filter(({ backup }) => backup.status === 'pending')
          .length,
        failedBackups: pendingBackups.filter(({ backup }) => backup.status === 'failed')
          .length,
      },
    });

    // Each item reads its blob and finishes its upload inside one tracked slot,
    // so logout drains those uploads before wiping either database.
    // After one offline deferral the rest defer too: each would sit out the same bound.
    let offline = false;
    const unlessOffline =
      (run: (context: AudioWorkContext) => Promise<RecoveryItemResult>) =>
      async (context: AudioWorkContext): Promise<RecoveryItemResult> => {
        if (offline) return 'deferred';
        const result = await run(context);
        offline ||= result === 'deferred';
        return result;
      };
    const recoveryItems = [
      ...pendingChunks.map((pending) =>
        unlessOffline((context) => recoverChunk(set, get, pending, context))
      ),
      ...pendingBackups.map((pending) =>
        unlessOffline((context) => recoverBackup(set, get, pending, context))
      ),
    ];
    const recoveryResults = await runRecoveryQueue(
      recoveryItems,
      (item) => runTrackedAudioWork(item, 'other'),
      { concurrency: RECOVERY_DISPATCH_CONCURRENCY }
    );

    recoveryResults.forEach((result) => {
      if (result.status === 'rejected') {
        logger.warn({
          event_type: 'HYDRATE_RECOVERY_ITEM_FAILED',
          details: { error: errorMessage(result.reason), source },
        });
      }
    });

    const countRecovered = (outcome: RecoveryItemResult): number =>
      recoveryResults.filter(
        (result) => result.status === 'fulfilled' && result.value === outcome
      ).length;
    const dispatched = countRecovered('processed');
    const abandoned = countRecovered('abandoned');
    const deferred = countRecovered('deferred');
    logger.info({
      event_type: 'LEDGER_BACKLOG_DISPATCHED',
      details: { source, dispatched, abandoned, deferred },
    });
    // Deferred rows are owed a later hydration; the connectivity-regained poke
    // re-drains. Not on rollback: a re-hydration there re-dispatches the legacy
    // store, which has no in-flight dedupe.
    if (deferred > 0 && source === 'active-ledger') set({ hydrated: false });

    if (retryAt !== Infinity) {
      logger.info({
        event_type: 'LEDGER_RECOVERY_DEFERRED',
        details: { source, retryInMs: Math.max(retryAt - Date.now(), 0) },
      });
      scheduleDeferredRecovery(set, get, retryAt);
    }

    if (abandoned > 0) {
      if (source === 'legacy-rollback') {
        void cleanupRecordingsV2(get);
      } else {
        void get().cleanupRecordingsTask();
      }
    }
  } catch (err) {
    logger.error({
      event_type: 'LEDGER_BACKLOG_DRAIN_FAILED',
      details: { error: errorMessage(err), source },
    });
    set({ hydrated: false });
  } finally {
    if ('token' in captureLock) releaseCaptureLock(captureLock.token);
  }
  return scanned ? 'scanned' : 'skipped';
};

/** Imports eligible legacy rows, then drains all recoverable ledger audio. */
export const hydrateV2 = async (
  set: SetState,
  get: GetState,
  currentUserRefId: string
) => {
  try {
    await importLegacyData(currentUserRefId);
  } catch (err) {
    logger.error({
      event_type: 'IMPORT_LEGACY_FAILED',
      details: { error: errorMessage(err) },
    });
    // Import is idempotent — unlatch so a later hydration retries it instead
    // of stranding un-imported legacy audio for the rest of the page lifetime.
    set({ hydrated: false });
  }

  return drainLedgerBacklogV2(set, get, currentUserRefId, 'active-ledger');
};
